import { Loading } from "../app/Status";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import {
  ASSET_KIND_LABELS,
  ASSET_SCORE_HOW_LABELS,
  assetScore,
  categorySeverity,
  CONDITION_SCORE_LABELS,
  CONDITION_SCORES,
  DECAY_CATEGORY,
  DEFECT_CATEGORIES,
  DEFECT_INTENSITIES,
  DEFECT_INTENSITY_LABELS,
  DEFECT_LOCATION_LABELS,
  DEFECT_LOCATIONS,
  DEFECT_SEVERITIES,
  DEFECT_SEVERITY_LABELS,
  defectScore,
  DISCIPLINE_LABELS,
  ASSESSMENT_STATUS_LABELS,
  EXTENT_CLASS_LABELS,
  EXTENT_CLASS_PICKS,
  extentClass,
  PROPOSAL_STATUS_LABELS,
  safetyNetOf,
  safetyNetText,
  SCORE_METHOD_LABELS,
  suggestParts,
  SYSTEM_CATALOG_ID,
  type ConditionAssetDto,
  type DefectCatalogItemDto,
  type DefectCatalogPartDto,
  type ConditionDefectDto,
  type ConditionProposalDto,
  type DefectIntensity,
  type DefectLocation,
  type DefectSeverity,
  type AssessmentDetailDto,
} from "@klantpunt/shared";
import { IconCamera, IconCheck, IconChevronLeft, IconDownload, IconFile, IconPlus, IconTrash } from "../app/icons";
import { Tabs } from "../app/Choice";
import { Meta } from "../app/Meta";
import { Modal } from "../app/Modal";
import {
  assessmentKey,
  type JudgementBody,
  useConditionField,
  useAssessmentDetail,
  usePendingConditionOps,
} from "../features/condition/useConditionField";
import { api, ApiError } from "../lib/api";
import { hasModule, useMe } from "../lib/auth";
import { thumbUrl, photoUrl } from "../lib/photos";
import { useToast } from "../lib/toast";

/**
 * Conditiemeting uitvoeren (docs/CONDITIEMETING.md §10.2 en §10.3): per asset gebreken vastleggen en
 * een oordeel geven (mobiel, ook offline), daarna controleren en afronden (desktop).
 *
 * De score volgt uit de gebreken volgens NEN 2767-1 (tabel 5, bijlage B); een correctie kan alleen met
 * reden. De gebreken komen uit de lijst van het bouwdeel dat bij de asset is gekozen.
 */
const errMsg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Er ging iets mis");
const pct = (r: number | null) => (r == null ? null : `${Math.round(r * 100)}%`);
const fmtDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });

export function ConditionFieldPage() {
  const { id = "" } = useParams();
  const { data: me } = useMe();
  if (!hasModule(me, "condition"))
    return (
      <div className="center">
        <p>Geen toegang tot de module Conditiemeting.</p>
      </div>
    );
  return <Field id={id} canEdit={hasModule(me, "condition", "edit")} />;
}

function Field({ id, canEdit }: { id: string; canEdit: boolean }) {
  const q = useAssessmentDetail(id);
  const { data: me } = useMe();
  const toast = useToast();
  const [tab, setTab] = useState<"field" | "review" | "proposals">("field");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const pending = usePendingConditionOps(id);
  const d = q.data;
  if (q.isPending) return <Loading page />;
  if (!d) return <p className="center">Deze meting bestaat niet (meer).</p>;
  const m = d.assessment;
  const editable = canEdit && m.status === "in_progress";
  const current = d.assets.find((a) => a.asset.id === open);
  const done = m.status === "completed";

  async function makePdf() {
    if (!d) return;
    setPdfBusy(true);
    try {
      const [{ buildConditionReport, conditionFilename }, web, building, proposals] = await Promise.all([
        import("../features/report/buildCondition"),
        import("../features/report/browser"),
        api<{ code: string; name: string }>(`/buildings/${d.assessment.buildingId}`),
        api<ConditionProposalDto[]>(`/condition/assessments/${id}/proposals`),
      ]);
      const res = await buildConditionReport({
        detail: d,
        building: { code: building.code, name: building.name },
        proposals,
        author: me?.user.name ?? "",
        date: new Date(),
        assets: await web.loadAssets(me?.org),
        loadPhoto: web.loadPhoto4x3,
      });
      web.download(res.bytes, conditionFilename(building.code, d.assessment.assessedOn));
      toast(`Rapport als PDF gemaakt (${res.pages} pagina's)`);
    } catch {
      toast("De PDF kon niet worden gemaakt. Probeer het opnieuw.");
    } finally {
      setPdfBusy(false);
    }
  }

  return (
    <div className="page condition-field">
      <Link to="/conditie" className="btn ghost small">
        <IconChevronLeft size={18} aria-hidden="true" />
        Conditiemeting
      </Link>
      <div className="titlerow">
        <h1 className="dot">Meting {m.assessedOn ? fmtDate(m.assessedOn) : ""}</h1>
        <span className="chip">{ASSESSMENT_STATUS_LABELS[m.status]}</span>
        <span className="btnrow">
          <button type="button" className="btn" disabled={pdfBusy} onClick={() => void makePdf()}>
            <IconFile size={20} aria-hidden="true" />
            {pdfBusy ? "PDF maken…" : "Rapport (PDF)"}
          </button>
          <a className="btn" href={`/api/condition/assessments/${id}/export.xlsx`} download>
            <IconDownload size={20} aria-hidden="true" />
            Excel
          </a>
        </span>
      </div>
      <p className="muted small">
        {m.assessor?.name ?? "Inspecteur"} ·{" "}
        {m.defectCatalog ? `${m.defectCatalog.name} (${m.defectCatalog.version})` : ""}
        {` · ${safetyNetText(safetyNetOf(m.instruction))} · ${SCORE_METHOD_LABELS[m.instruction.method ?? "table5"].toLowerCase()}`}
      </p>
      {pending > 0 && (
        <p className="note" role="status">
          {pending} wijziging{pending === 1 ? "" : "en"} wacht{pending === 1 ? "" : "en"} op verbinding.
        </p>
      )}
      <Tabs
        label="Onderdeel"
        items={[
          { value: "field", label: "Assets" },
          { value: "review", label: `Controle${d.open > 0 ? ` (${d.open} open)` : ""}` },
          ...(done ? [{ value: "proposals" as const, label: "MJOP-voorstellen" }] : []),
        ]}
        value={tab}
        onChange={setTab}
      />
      {tab === "field" ? (
        <AssetList d={d} onOpen={setOpen} />
      ) : tab === "review" || !done ? (
        <Review d={d} canComplete={editable} pending={pending} onOpen={setOpen} />
      ) : (
        <Proposals id={id} />
      )}
      {current && (
        <AssetSheet
          key={current.asset.id}
          d={d}
          a={current}
          editable={editable}
          canFinding={hasModule(me, "issues", "edit")}
          canEstimate={hasModule(me, "mjop", "edit")}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

const scoreText = (a: ConditionAssetDto) =>
  a.notInspectable
    ? "Niet te inspecteren"
    : a.effectiveScore == null
      ? "Nog geen oordeel"
      : a.overrideScore != null
        ? `Conditie ${a.effectiveScore} (correctie)`
        : `Conditie ${a.effectiveScore}${a.how ? ` · ${ASSET_SCORE_HOW_LABELS[a.how]}` : ""}`;
const decimal = (n: number) => n.toLocaleString("nl-NL", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const euro = (cents: number) =>
  (cents / 100).toLocaleString("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

function AssetList({ d, onOpen }: { d: AssessmentDetailDto; onOpen: (id: string) => void }) {
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return d.assets.filter((a) => !t || `${a.asset.code} ${a.asset.name}`.toLowerCase().includes(t));
  }, [d, q]);
  return (
    <section className="panel" aria-label="Assets in deze meting">
      <input
        type="search"
        aria-label="Assets zoeken"
        placeholder="Zoek op code of naam…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <ul className="list">
        {list.map((a) => (
          <li key={a.asset.id}>
            <button type="button" className="profile-row linkrow" onClick={() => onOpen(a.asset.id)}>
              <span
                className={`condscore cond-${a.notInspectable ? "x" : (a.effectiveScore ?? "none")}`}
                aria-hidden="true"
              >
                {a.notInspectable ? "–" : (a.effectiveScore ?? "?")}
              </span>
              <span className="grow">
                <strong>
                  {a.asset.code} {a.asset.name}
                </strong>
                <Meta
                  className="muted small block"
                  items={[
                    ASSET_KIND_LABELS[a.asset.assetKind],
                    partName(d, a.asset.catalogPartCode),
                    `${a.defects.length} gebrek${a.defects.length === 1 ? "" : "en"}`,
                    scoreText(a),
                  ]}
                />
              </span>
            </button>
          </li>
        ))}
      </ul>
      {!list.length && <p className="muted">Geen assets.</p>}
    </section>
  );
}

const partName = (d: AssessmentDetailDto, code: string | null) => {
  const p = code ? d.catalogParts.find((x) => x.code === code) : undefined;
  return p ? `${p.code} ${p.name}` : null;
};

/** Gebreken die bij deze asset passen: die van het gekozen bouwdeel plus de generieke. */
function itemsFor(d: AssessmentDetailDto, a: ConditionAssetDto) {
  const code = a.asset.catalogPartCode;
  return d.catalogItems.filter((i) =>
    i.catalogId === SYSTEM_CATALOG_ID ? true : code ? i.partCode === code : !i.partCode,
  );
}

/** Conditie van één gebrek bij zijn omvang (tabel 5); null zonder intensiteit of omvang, of facultatief. */
function oneScore(x: {
  severity: DefectSeverity;
  intensity: DefectIntensity | null;
  extentPercent: number | null;
  isOptional?: boolean;
}) {
  if (x.isOptional || !x.intensity || x.extentPercent == null || !(x.extentPercent > 0)) return null;
  return defectScore(x.severity, x.intensity, extentClass(x.extentPercent));
}

function AssetSheet({
  d,
  a,
  editable,
  canFinding,
  canEstimate,
  onClose,
}: {
  d: AssessmentDetailDto;
  a: ConditionAssetDto;
  editable: boolean;
  canFinding: boolean;
  canEstimate: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const ops = useConditionField(d.assessment.id, d.assessment.buildingId);
  const [adding, setAdding] = useState<DefectPreset | null>(null);
  const [finding, setFinding] = useState<ConditionDefectDto | null>(null);
  const [score, setScore] = useState(a.overrideScore?.toString() ?? "");
  const [reason, setReason] = useState(a.overrideReason ?? "");
  const [notInspectable, setNotInspectable] = useState(a.notInspectable);
  const [inspected, setInspected] = useState(a.inspected);
  const [remarks, setRemarks] = useState(a.remarks ?? "");
  const label = `${a.asset.code} ${a.asset.name}`;
  const decayItem =
    itemsFor(d, a).find((i) => i.defectCategory === DECAY_CATEGORY && !i.requiredIntensity) ??
    itemsFor(d, a).find((i) => i.defectCategory === DECAY_CATEGORY && i.requiredIntensity === a.decayIntensity);
  const hasDecay = a.defects.some((x) => x.defectCategory === DECAY_CATEGORY);
  const report = (s: string, ok: string) =>
    toast(s === "queued" ? `${ok} op dit apparaat; wordt verstuurd zodra er verbinding is` : ok);

  async function saveJudgement(e: FormEvent) {
    e.preventDefault();
    if (score && !reason.trim()) return toast("Geef een reden voor de correctie");
    try {
      const s = await ops.setJudgement(
        a.asset.id,
        {
          notInspectable,
          inspected: !notInspectable && inspected,
          remarks: remarks.trim() || null,
          overrideScore: notInspectable || !score ? null : Number(score),
          overrideReason: notInspectable || !score ? null : reason.trim(),
        },
        `Oordeel ${a.asset.code}`,
      );
      report(s, "Oordeel opgeslagen");
    } catch (err) {
      toast(errMsg(err));
    }
  }

  return (
    <Modal title={label} onClose={onClose}>
      <div className="stack">
        <p className="small">
          <Meta
            items={[
              ASSET_KIND_LABELS[a.asset.assetKind],
              a.asset.quantity != null && `${a.asset.quantity.toLocaleString("nl-NL")} ${a.asset.unit ?? ""}`,
              a.theoreticalScore != null ? (
                <>
                  theoretische conditie <strong>{a.theoreticalScore}</strong> ({pct(a.ageRatio)} van de levensduur)
                </>
              ) : (
                <span className="muted">{!a.asset.yearBuilt ? "bouwjaar onbekend" : "levensduur onbekend"}</span>
              ),
            ]}
          />
        </p>

        {d.catalogParts.length > 0 && <PartPicker d={d} a={a} editable={editable} />}

        <div className="condresult" role="status">
          <span
            className={`condscore cond-${a.notInspectable ? "x" : (a.effectiveScore ?? "none")}`}
            aria-hidden="true"
          >
            {a.notInspectable ? "–" : (a.effectiveScore ?? "?")}
          </span>
          <span className="grow small">
            <strong>{scoreText(a)}</strong>
            <Meta
              className="muted block"
              items={[
                a.calculatedScore != null && a.overrideScore != null && `berekend ${a.calculatedScore}`,
                a.calculatedOutcome != null && a.how === "annexB" && `uitkomst ${decimal(a.calculatedOutcome)}`,
                a.overrideReason,
              ]}
            />
          </span>
        </div>

        {a.decayIntensity && editable && (
          <div className="note stack">
            <span className="small">
              Vangnet: deze asset is ouder dan{" "}
              {a.decayIntensity === "final" ? "87,5" : a.decayIntensity === "advanced" ? "75" : "50"}% van zijn
              levensduur. Kun je een deel niet zien, leg dan verval vast: gering,{" "}
              {DEFECT_INTENSITY_LABELS[a.decayIntensity].toLowerCase()}, over het deel dat je niet kunt beoordelen.
              {hasDecay && " Verval is vastgelegd."}
            </span>
            {!hasDecay && decayItem && (
              <div>
                <button
                  type="button"
                  className="btn small"
                  onClick={() => setAdding({ itemId: decayItem.id, intensity: a.decayIntensity, extent: 100 })}
                >
                  <IconPlus size={16} aria-hidden="true" />
                  Verval
                </button>
              </div>
            )}
          </div>
        )}

        <section aria-label="Gebreken" className="stack">
          <p className="label">Gebreken {a.defects.length}</p>
          {!a.defects.length && (
            <p className="muted small">Nog geen gebreken. Geen gebreken gevonden? Kies hieronder "Geen gebreken".</p>
          )}
          <ul className="list compact">
            {a.defects.map((x) => {
              const host = x.stackedOnId ? a.defects.find((y) => y.id === x.stackedOnId) : undefined;
              const s1 = oneScore(x);
              return (
                <li key={x.id} className="profile-row">
                  <span className="grow small">
                    <strong>
                      {x.defectCode && <span className="muted">{x.defectCode} </span>}
                      {x.title}
                    </strong>
                    {x.isOptional && <span className="chip small">facultatief</span>}
                    <Meta
                      className="muted block"
                      items={[
                        x.defectLocation !== "asset" &&
                          `${DEFECT_LOCATION_LABELS[x.defectLocation]}${x.componentNote ? `: ${x.componentNote}` : ""}`,
                        DEFECT_SEVERITY_LABELS[x.severity],
                        x.intensity && DEFECT_INTENSITY_LABELS[x.intensity],
                        x.extentPercent != null && `omvang ${x.extentPercent}%`,
                        s1 != null && `conditie ${s1}`,
                        host && `zelfde plek als ${host.title}`,
                        x.description && x.title !== x.description && x.description,
                      ]}
                    />
                    <DefectPhotos d={d} x={x} editable={editable} />
                    {x.findingId ? (
                      <Link
                        className="small block"
                        to={`/gebouwen/${d.assessment.buildingId}?bevinding=${x.findingId}`}
                      >
                        Bevinding bekijken
                      </Link>
                    ) : (
                      canFinding && (
                        <button type="button" className="btn small" onClick={() => setFinding(x)}>
                          Bevinding maken
                        </button>
                      )
                    )}
                  </span>
                  {editable && (
                    <button
                      type="button"
                      className="btn ico ghost"
                      aria-label={`${x.title} verwijderen`}
                      title="Verwijderen"
                      onClick={async () => {
                        try {
                          report(await ops.removeDefect(x.id, `Gebrek ${a.asset.code}`), "Gebrek verwijderd");
                        } catch (err) {
                          toast(errMsg(err));
                        }
                      }}
                    >
                      <IconTrash size={18} aria-hidden="true" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          {editable &&
            (adding ? (
              <DefectForm
                d={d}
                a={a}
                preset={adding}
                onCancel={() => setAdding(null)}
                onSaved={(s) => {
                  setAdding(null);
                  report(s, "Gebrek vastgelegd");
                }}
              />
            ) : (
              <div>
                <button type="button" className="btn" onClick={() => setAdding({})}>
                  <IconPlus size={18} aria-hidden="true" />
                  Gebrek
                </button>
              </div>
            ))}
        </section>

        {finding && <FindingForm d={d} x={finding} canEstimate={canEstimate} onDone={() => setFinding(null)} />}

        <form className="stack" aria-label="Oordeel" onSubmit={saveJudgement}>
          <p className="label">Oordeel</p>
          <fieldset className="stack" disabled={!editable}>
            {!a.defects.length && (
              <label className="field inline">
                <input
                  type="checkbox"
                  checked={inspected && !notInspectable}
                  disabled={notInspectable}
                  onChange={(e) => setInspected(e.target.checked)}
                />
                <span>Geen gebreken (geïnspecteerd, conditie 1)</span>
              </label>
            )}
            <label className="field inline">
              <input type="checkbox" checked={notInspectable} onChange={(e) => setNotInspectable(e.target.checked)} />
              <span>Niet te inspecteren (bijv. niet bereikbaar; telt niet mee)</span>
            </label>
            {!notInspectable && (
              <div className="formgrid">
                <label className="field">
                  <span className="label">Correctie op de score</span>
                  <select value={score} onChange={(e) => setScore(e.target.value)}>
                    <option value="">Geen: de berekende score telt</option>
                    {CONDITION_SCORES.map((n) => (
                      <option key={n} value={n}>
                        {n} · {CONDITION_SCORE_LABELS[n]}
                      </option>
                    ))}
                  </select>
                </label>
                {score && (
                  <label className="field">
                    <span className="label">Reden *</span>
                    <input value={reason} onChange={(e) => setReason(e.target.value)} />
                    <span className="hint">De correctie en de reden staan in het rapport.</span>
                  </label>
                )}
              </div>
            )}
            <label className="field">
              <span className="label">Opmerkingen</span>
              <textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
            </label>
          </fieldset>
          {editable && (
            <div>
              <button type="submit" className="btn primary">
                Oordeel opslaan
              </button>
            </div>
          )}
        </form>
      </div>
    </Modal>
  );
}

/**
 * Bouwdeel uit de gebrekenlijst kiezen op naam (NL-SfB in de norm is een andere versie dan NLBE-SfB in de
 * app). De koppeltabel geeft alleen voorstellen op de NLBE-code van de asset.
 */
function PartPicker({ d, a, editable }: { d: AssessmentDetailDto; a: ConditionAssetDto; editable: boolean }) {
  const toast = useToast();
  const ops = useConditionField(d.assessment.id, d.assessment.buildingId);
  const part = d.catalogParts.find((p) => p.code === a.asset.catalogPartCode);
  const [picking, setPicking] = useState(!part);
  const [q, setQ] = useState("");
  const [life, setLife] = useState("");
  const suggested = useMemo(() => suggestParts(d.catalogParts, a.asset.elementCode).slice(0, 4), [d, a]);
  const found = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return d.catalogParts
      .filter((p) => `${p.code} ${p.name} ${p.specs.map((s) => s.text).join(" ")}`.toLowerCase().includes(t))
      .slice(0, 8);
  }, [d, q]);
  async function save(body: JudgementBody, ok: string) {
    try {
      const s = await ops.setJudgement(a.asset.id, body, `Bouwdeel ${a.asset.code}`);
      toast(s === "queued" ? `${ok} op dit apparaat; wordt verstuurd zodra er verbinding is` : ok);
    } catch (err) {
      toast(errMsg(err));
    }
  }
  const choose = (code: string) => {
    setPicking(false);
    setQ("");
    void save({ catalogPartCode: code, catalogPartSpec: null }, "Bouwdeel gekozen");
  };
  const pick = (p: DefectCatalogPartDto) => (
    <button key={p.code} type="button" className="chip toggle" onClick={() => choose(p.code)}>
      {p.code} {p.name}
    </button>
  );
  if (part && !picking)
    return (
      <section className="stack" aria-label="Bouwdeel uit de gebrekenlijst">
        <div className="titlerow">
          <p className="small grow">
            Gebrekenlijst:{" "}
            <strong>
              {part.code} {part.name}
            </strong>
          </p>
          {editable && (
            <button type="button" className="btn small ghost" onClick={() => setPicking(true)}>
              Wijzigen
            </button>
          )}
        </div>
        {part.specs.length > 1 && (
          <label className="field">
            <span className="label">Specificatie</span>
            <select
              disabled={!editable}
              value={a.asset.catalogPartSpec ?? ""}
              onChange={(e) => void save({ catalogPartSpec: e.target.value || null }, "Specificatie opgeslagen")}
            >
              <option value="">Niet gekozen</option>
              {part.specs.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.text}
                </option>
              ))}
            </select>
          </label>
        )}
        {editable && !a.asset.lifespanYears && part.lifespans.length > 0 && (
          <div className="row">
            <label className="field grow">
              <span className="label">Levensduur uit de lijst (voor vangnet en MJOP)</span>
              <select value={life} onChange={(e) => setLife(e.target.value)}>
                <option value="">Kies…</option>
                {part.lifespans.map((l, k) => (
                  <option key={k} value={l.years}>
                    {l.years} jaar · {l.text}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="btn"
              disabled={!life}
              onClick={() => void save({ lifespanYears: Number(life) }, "Levensduur overgenomen")}
            >
              Overnemen
            </button>
          </div>
        )}
      </section>
    );
  return (
    <section className="stack" aria-label="Bouwdeel uit de gebrekenlijst">
      <p className="label">Bouwdeel in de gebrekenlijst</p>
      {suggested.length > 0 && (
        <div className="stack">
          <span className="hint">Voorstel op de NL-SfB-code {a.asset.elementCode}:</span>
          <div className="chips">{suggested.map(pick)}</div>
        </div>
      )}
      <label className="field">
        <span className="label">Zoek op naam</span>
        <input
          type="search"
          value={q}
          disabled={!editable}
          onChange={(e) => setQ(e.target.value)}
          placeholder="bijv. ketel, dakbedekking, verlichting"
        />
      </label>
      {found.length > 0 && <div className="chips">{found.map(pick)}</div>}
      {q.trim() && !found.length && <p className="muted small">Geen bouwdeel gevonden.</p>}
      {part && (
        <div>
          <button type="button" className="btn small ghost" onClick={() => setPicking(false)}>
            Annuleren
          </button>
        </div>
      )}
      {!part && <p className="hint">Zonder bouwdeel zie je alleen de generieke gebreken.</p>}
    </section>
  );
}

interface DefectPreset {
  itemId?: string;
  intensity?: DefectIntensity | null;
  extent?: number;
}

const SEVERITY_ORDER: DefectSeverity[] = ["critical", "serious", "minor"];

function DefectForm({
  d,
  a,
  preset,
  onCancel,
  onSaved,
}: {
  d: AssessmentDetailDto;
  a: ConditionAssetDto;
  preset: DefectPreset;
  onCancel: () => void;
  onSaved: (status: string) => void;
}) {
  const toast = useToast();
  const ops = useConditionField(d.assessment.id, d.assessment.buildingId);
  const [loc, setLoc] = useState<DefectLocation>("asset");
  const [note, setNote] = useState("");
  const [itemId, setItemId] = useState(preset.itemId ?? "");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [severity, setSeverity] = useState<DefectSeverity | "">("");
  const [intensity, setIntensity] = useState<DefectIntensity | "">(preset.intensity ?? "");
  const [extent, setExtent] = useState(preset.extent != null ? String(preset.extent) : "");
  const [affected, setAffected] = useState("");
  const [stackedOn, setStackedOn] = useState("");
  const [photos, setPhotos] = useState<{ file: File; url: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const urls = useRef<string[]>([]);
  useEffect(() => () => urls.current.forEach((u) => URL.revokeObjectURL(u)), []);
  const all = itemsFor(d, a);
  const item = all.find((i) => i.id === itemId);
  const part = d.catalogParts.find((p) => p.code === a.asset.catalogPartCode);
  const own = item?.defectType === "own";
  const optional = item?.defectType === "optional";
  // Regels §4: vaste ernst bij veroudering, bij een gebrek uit de lijst en bij een eigen gebrek met soort.
  const fixedSeverity: DefectSeverity | null =
    item?.defectType === "component_ageing"
      ? "serious"
      : item?.defectType === "subcomponent_ageing"
        ? "minor"
        : (item?.severity ?? (own ? categorySeverity(category) : null));
  const fixedIntensity = item?.requiredIntensity ?? null;
  const sev = fixedSeverity ?? (severity || null);
  const int = fixedIntensity ?? (intensity || null);
  const needsText = own || (optional && !item?.partCode);
  const pctValue = extent.trim() ? Number(extent.replace(",", ".")) : null;
  const hosts = a.defects.filter((x) => !x.isOptional);
  // Plek: veroudering hoort bij een component of onderdeel.
  const locked: DefectLocation | null =
    item?.defectType === "component_ageing"
      ? "component"
      : item?.defectType === "subcomponent_ageing"
        ? "subcomponent"
        : null;
  const where = locked ?? loc;
  const componentChoices = [...a.asset.parts.map((p) => `${p.code} ${p.name}`), ...(part?.components ?? [])];

  const groups = useMemo(() => {
    const generic = all.filter((i) => i.catalogId === SYSTEM_CATALOG_ID);
    const listed = all.filter((i) => i.catalogId !== SYSTEM_CATALOG_ID);
    const out: { label: string; items: DefectCatalogItemDto[] }[] = [];
    for (const s of SEVERITY_ORDER) {
      const bySev = listed.filter((i) => i.defectType !== "optional" && i.severity === s);
      const cats = [...new Set(bySev.map((i) => i.defectCategory ?? ""))];
      for (const c of cats)
        out.push({
          label: `${DEFECT_SEVERITY_LABELS[s]}${c ? ` · ${c}` : ""}`,
          items: bySev.filter((i) => (i.defectCategory ?? "") === c),
        });
    }
    const rest = listed.filter((i) => i.defectType !== "optional" && !i.severity);
    if (rest.length) out.push({ label: "Overig", items: rest });
    const opt = listed.filter((i) => i.defectType === "optional");
    if (opt.length) out.push({ label: "Facultatief (telt niet mee)", items: opt });
    out.push({ label: "Algemeen (elke asset)", items: generic });
    return out;
  }, [all]);

  // Live: conditie van dit gebrek en wat de asset wordt.
  const preview = useMemo(() => {
    if (!item || optional || !sev || !int || pctValue == null || !(pctValue > 0)) return null;
    const self = defectScore(sev, int, extentClass(Math.min(100, pctValue)));
    const asset = assetScore(
      [
        ...a.defects,
        { id: "new", severity: sev, intensity: int, extentPercent: pctValue, stackedOnId: stackedOn || null },
      ],
      d.assessment.instruction.method ?? "table5",
    );
    return { self, asset };
  }, [item, optional, sev, int, pctValue, stackedOn, a, d]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    const id = crypto.randomUUID();
    try {
      let s = await ops.addDefect(
        {
          id,
          assetId: a.asset.id,
          catalogDefectId: item?.id ?? null,
          description: description.trim() || null,
          defectLocation: where,
          componentNote: where === "asset" ? null : note.trim() || null,
          severity: fixedSeverity ? null : severity || null,
          intensity: fixedIntensity || optional ? null : intensity || null,
          extentPercent: optional ? null : pctValue,
          defectCategory: own ? category || null : null,
          stackedOnId: optional ? null : stackedOn || null,
        },
        `Gebrek ${a.asset.code}`,
      );
      // Foto's in dezelfde handeling: na het gebrek in de wachtrij, dus in de goede volgorde.
      for (const p of photos) if ((await ops.addPhoto(id, p.file)) === "queued") s = "queued";
      onSaved(s);
    } catch (err) {
      toast(errMsg(err));
      setBusy(false);
    }
  }

  return (
    <form className="obcard stack" aria-label="Gebrek toevoegen" onSubmit={submit}>
      <label className="field">
        <span className="label">Gebrek</span>
        <select value={itemId} onChange={(e) => setItemId(e.target.value)}>
          <option value="">Kies…</option>
          {groups.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.items.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.description}
                  {i.requiredIntensity ? ` (${DEFECT_INTENSITY_LABELS[i.requiredIntensity].toLowerCase()})` : ""}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        {!part && d.catalogParts.length > 0 && (
          <span className="hint">Kies eerst het bouwdeel voor de gebreken uit de lijst.</span>
        )}
      </label>

      {item && (
        <>
          {own && (
            <div className="formgrid">
              <label className="field">
                <span className="label">Soort gebrek</span>
                <select value={category} onChange={(e) => setCategory(e.target.value)}>
                  <option value="">Andere soort: ernst zelf kiezen</option>
                  {SEVERITY_ORDER.map((s) => (
                    <optgroup key={s} label={DEFECT_SEVERITY_LABELS[s]}>
                      {DEFECT_CATEGORIES.filter((c) => c.severity === s).map((c) => (
                        <option key={c.code} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <span className="hint">De soort bepaalt de ernst (NEN 2767-1).</span>
              </label>
              {!fixedSeverity && (
                <label className="field">
                  <span className="label">Ernst</span>
                  <select value={severity} onChange={(e) => setSeverity(e.target.value as DefectSeverity)}>
                    <option value="">Kies…</option>
                    {DEFECT_SEVERITIES.map((s) => (
                      <option key={s} value={s}>
                        {DEFECT_SEVERITY_LABELS[s]}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}
          {needsText && (
            <label className="field">
              <span className="label">Toelichting *</span>
              <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
            </label>
          )}
          {sev && !optional && (
            <p className="small">
              Ernst <strong>{DEFECT_SEVERITY_LABELS[sev].toLowerCase()}</strong>
              {fixedSeverity && <span className="muted"> (ligt vast)</span>}
            </p>
          )}

          <fieldset className="mjop-types">
            <legend className="label">Waar zit het gebrek?</legend>
            {DEFECT_LOCATIONS.map((l) => (
              <label key={l} className="chip toggle">
                <input
                  type="radio"
                  name="loc"
                  checked={where === l}
                  disabled={Boolean(locked) && locked !== l}
                  onChange={() => setLoc(l)}
                />
                {DEFECT_LOCATION_LABELS[l]}
              </label>
            ))}
          </fieldset>
          {where !== "asset" && (
            <div className="stack">
              {componentChoices.length > 0 && (
                <div className="chips" aria-label="Voorbeelden">
                  {componentChoices.slice(0, 12).map((c) => (
                    <button
                      key={c}
                      type="button"
                      className="chip toggle"
                      aria-pressed={note === c}
                      onClick={() => setNote(c)}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
              <label className="field">
                <span className="label">{where === "component" ? "Welke component?" : "Welk onderdeel?"} *</span>
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={where === "component" ? "bijv. brander" : "bijv. kitwerk"}
                />
              </label>
            </div>
          )}

          {!optional && (
            <>
              <fieldset className="mjop-types">
                <legend className="label">Intensiteit{fixedIntensity ? " (ligt vast)" : ""}</legend>
                {DEFECT_INTENSITIES.map((i) => (
                  <label key={i} className="chip toggle">
                    <input
                      type="radio"
                      name="int"
                      checked={int === i}
                      disabled={Boolean(fixedIntensity) && fixedIntensity !== i}
                      onChange={() => setIntensity(i)}
                    />
                    {DEFECT_INTENSITY_LABELS[i]}
                  </label>
                ))}
              </fieldset>
              <fieldset className="mjop-types">
                <legend className="label">Omvang (% van de asset)</legend>
                {([1, 2, 3, 4, 5] as const).map((c) => (
                  <label key={c} className="chip toggle">
                    <input
                      type="radio"
                      name="cls"
                      checked={pctValue != null && pctValue > 0 && extentClass(pctValue) === c}
                      onChange={() => {
                        setExtent(String(EXTENT_CLASS_PICKS[c]));
                        setAffected("");
                      }}
                    />
                    {EXTENT_CLASS_LABELS[c]}
                  </label>
                ))}
              </fieldset>
              <div className="formgrid">
                <label className="field">
                  <span className="label">Omvang in %</span>
                  <input inputMode="decimal" value={extent} onChange={(e) => setExtent(e.target.value)} />
                </label>
                {a.asset.quantity != null && a.asset.quantity > 0 && (
                  <label className="field">
                    <span className="label">
                      Of: aangetast van {a.asset.quantity.toLocaleString("nl-NL")} {a.asset.unit ?? ""}
                    </span>
                    <input
                      inputMode="decimal"
                      value={affected}
                      onChange={(e) => {
                        setAffected(e.target.value);
                        const n = Number(e.target.value.replace(",", "."));
                        if (Number.isFinite(n) && n > 0)
                          setExtent(String(Math.min(100, Math.round((n / a.asset.quantity!) * 1000) / 10)));
                      }}
                    />
                  </label>
                )}
              </div>
              {hosts.length > 0 && (
                <label className="field">
                  <span className="label">Zelfde plek als een ander gebrek (gestapeld)</span>
                  <select value={stackedOn} onChange={(e) => setStackedOn(e.target.value)}>
                    <option value="">Nee, een andere plek</option>
                    {hosts.map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.title}
                        {h.extentPercent != null ? ` (${h.extentPercent}%)` : ""}
                      </option>
                    ))}
                  </select>
                  <span className="hint">Op dezelfde plek telt alleen het ergste gebrek mee.</span>
                </label>
              )}
              {preview && (
                <p className="note small" role="status">
                  Dit gebrek: conditie <strong>{preview.self}</strong>. De asset wordt{" "}
                  <strong>{preview.asset.score}</strong> ({ASSET_SCORE_HOW_LABELS[preview.asset.how]}
                  {preview.asset.how === "annexB" ? `, uitkomst ${decimal(preview.asset.outcome)}` : ""}).
                </p>
              )}
            </>
          )}
        </>
      )}
      <div className="field">
        <span className="label">Foto's</span>
        <span className="defectphotos">
          {photos.map((p, i) => (
            <span key={p.url} className="ph">
              <img src={p.url} alt={`Foto ${i + 1}`} />
              <button
                type="button"
                className="btn ico ghost small"
                aria-label={`Foto ${i + 1} verwijderen`}
                onClick={() => setPhotos((ps) => ps.filter((x) => x !== p))}
              >
                <IconTrash size={16} aria-hidden="true" />
              </button>
            </span>
          ))}
          <label className="btn small">
            <IconCamera size={18} aria-hidden="true" />
            Foto
            <input
              type="file"
              accept="image/*"
              hidden
              aria-label="Foto bij dit gebrek"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (!f) return;
                const url = URL.createObjectURL(f);
                urls.current.push(url);
                setPhotos((ps) => [...ps, { file: f, url }]);
              }}
            />
          </label>
        </span>
      </div>
      <div className="btnrow">
        <button type="submit" className="btn primary" disabled={busy || !itemId}>
          Gebrek opslaan
        </button>
        <button type="button" className="btn ghost" onClick={onCancel}>
          Annuleren
        </button>
      </div>
    </form>
  );
}

function BuildingScore({ d }: { d: AssessmentDetailDto }) {
  const s = d.building;
  return (
    <div className="stack" aria-label="Gebouwscore">
      <div className="condresult">
        <span className={`condscore cond-${s.score ?? "none"}`} aria-hidden="true">
          {s.score ?? "?"}
        </span>
        <span className="grow small">
          <strong>Gebouwscore {s.score ?? "nog niet te berekenen"}</strong>
          <Meta
            className="muted block"
            items={[
              s.outcome != null && `uitkomst ${decimal(s.outcome)}`,
              s.valueCents > 0 && `vervangingswaarde ${euro(s.valueCents)}`,
              ...s.disciplines.map((x) => `${DISCIPLINE_LABELS[x.discipline]} ${x.score}`),
            ]}
          />
        </span>
      </div>
      <p className="hint">
        Gewogen op vervangingswaarde met de correctiefactoren uit NEN 2767-1 bijlage B.
        {s.missingValue > 0 &&
          ` ${s.missingValue} asset${s.missingValue === 1 ? "" : "s"} zonder vervangingspost in het MJOP ${s.missingValue === 1 ? "telt" : "tellen"} niet mee.`}
      </p>
    </div>
  );
}

function Review({
  d,
  canComplete,
  pending,
  onOpen,
}: {
  d: AssessmentDetailDto;
  canComplete: boolean;
  pending: number;
  onOpen: (id: string) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  async function complete() {
    if (!confirm("Meting afronden? Daarna ligt hij vast en krijgen de assets hun conditie.")) return;
    setBusy(true);
    try {
      const r = await api<AssessmentDetailDto>(`/condition/assessments/${d.assessment.id}/complete`, {
        method: "POST",
      });
      qc.setQueryData(assessmentKey(d.assessment.id), r);
      void qc.invalidateQueries({ queryKey: ["building"] });
      void qc.invalidateQueries({ queryKey: ["condition-assessments"] });
      toast("Meting afgerond");
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="panel stack" aria-label="Controle">
      <BuildingScore d={d} />
      <div className="tscroll">
        <table className="it">
          <thead>
            <tr>
              <th>Asset</th>
              <th className="num">Gebreken</th>
              <th className="num">Berekend</th>
              <th className="num">Correctie</th>
              <th className="num">Theoretisch</th>
              <th className="num">Telt als</th>
              <th>Waarschuwingen</th>
            </tr>
          </thead>
          <tbody>
            {d.assets.map((a) => (
              <tr key={a.asset.id} onClick={() => onOpen(a.asset.id)}>
                <th scope="row">
                  <button type="button" className="linkbtn" onClick={() => onOpen(a.asset.id)}>
                    {a.asset.code} {a.asset.name}
                  </button>
                </th>
                <td className="num">{a.defects.length}</td>
                <td className="num">{a.notInspectable ? "n.v.t." : (a.calculatedScore ?? "—")}</td>
                <td className="num">{a.overrideScore ?? "—"}</td>
                <td className="num">{a.theoreticalScore ?? "—"}</td>
                <td className="num">
                  <strong>{a.effectiveScore ?? "—"}</strong>
                </td>
                <td className="small">{a.warnings.join(" · ") || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {d.assessment.status === "completed" ? (
        <p className="small">Afgerond. De assets hebben hun conditie gekregen; de meting ligt vast.</p>
      ) : (
        canComplete && (
          <div className="btnrow">
            <button
              type="button"
              className="btn primary"
              disabled={busy || d.open > 0 || pending > 0}
              onClick={() => void complete()}
            >
              Meting afronden
            </button>
            <span className="hint">
              {d.open > 0
                ? `Nog ${d.open} asset${d.open === 1 ? "" : "s"} zonder oordeel.`
                : pending > 0
                  ? "Eerst synchroniseren."
                  : "Alle assets hebben een oordeel."}
            </span>
          </div>
        )
      )}
    </section>
  );
}

/** Foto's bij een gebrek (§10.2): via de wachtrij, ook zonder bereik. */
function DefectPhotos({ d, x, editable }: { d: AssessmentDetailDto; x: ConditionDefectDto; editable: boolean }) {
  const toast = useToast();
  const ops = useConditionField(d.assessment.id, d.assessment.buildingId);
  const photos = x.photos ?? [];
  if (!photos.length && !editable) return null;
  return (
    <span className="defectphotos">
      {photos.map((f) => (
        <span key={f} className="ph">
          <a href={photoUrl(f)} target="_blank" rel="noopener">
            <img src={thumbUrl(f)} alt={`Foto bij ${x.title}`} />
          </a>
          {editable && (
            <button
              type="button"
              className="btn ico ghost small"
              aria-label={`Foto bij ${x.title} verwijderen`}
              onClick={() => void ops.removePhoto(x.id, f).catch((e: unknown) => toast(errMsg(e)))}
            >
              <IconTrash size={16} aria-hidden="true" />
            </button>
          )}
        </span>
      ))}
      {editable && (
        <label className="btn small">
          <IconCamera size={18} aria-hidden="true" />
          Foto
          <input
            type="file"
            accept="image/*"
            hidden
            aria-label={`Foto bij ${x.title}`}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (!f) return;
              try {
                const s = await ops.addPhoto(x.id, f);
                toast(
                  s === "queued"
                    ? "Foto bewaard op dit apparaat; wordt verstuurd zodra er verbinding is"
                    : "Foto toegevoegd",
                );
              } catch (err) {
                toast(errMsg(err));
              }
            }}
          />
        </label>
      )}
    </span>
  );
}

/** Gebrek → bevinding (§8), met een optionele raming die één herstelpost in het MJOP maakt. */
function FindingForm({
  d,
  x,
  canEstimate,
  onDone,
}: {
  d: AssessmentDetailDto;
  x: ConditionDefectDto;
  canEstimate: boolean;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [euro, setEuro] = useState("");
  const [year, setYear] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    const cents = euro.trim() ? Math.round(Number(euro.replace(/\./g, "").replace(",", ".")) * 100) : null;
    if (cents != null && !Number.isFinite(cents)) return toast("Vul een geldig bedrag in");
    setBusy(true);
    try {
      const r = await api<{ findingId: string; code: string }>(`/condition/defects/${x.id}/finding`, {
        method: "POST",
        body: {
          ...(cents != null ? { estimatedCostCents: cents } : {}),
          ...(cents != null && year ? { plannedRepairYear: Number(year) } : {}),
        },
      });
      await qc.invalidateQueries({ queryKey: assessmentKey(d.assessment.id) });
      void qc.invalidateQueries({ queryKey: ["building"] });
      toast(`Bevinding ${r.code} gemaakt`);
      onDone();
    } catch (err) {
      toast(errMsg(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="panel stack" aria-label="Bevinding maken" onSubmit={submit}>
      <p className="label">Bevinding maken: {x.title}</p>
      <p className="hint">
        De prioriteit volgt uit de ernst ({DEFECT_SEVERITY_LABELS[x.severity].toLowerCase()}). De bevinding komt op de
        plek van het gebrek of de asset.
      </p>
      {canEstimate && (
        <div className="row">
          <label className="field">
            <span className="label">Raming herstel (€, excl. btw, optioneel)</span>
            <input inputMode="decimal" value={euro} onChange={(e) => setEuro(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Jaar herstel</span>
            <input
              inputMode="numeric"
              placeholder={String(new Date().getFullYear())}
              value={year}
              onChange={(e) => setYear(e.target.value)}
              disabled={!euro.trim()}
            />
          </label>
        </div>
      )}
      <div className="btnrow">
        <button type="submit" className="btn primary" disabled={busy}>
          Bevinding maken
        </button>
        <button type="button" className="btn ghost" onClick={onDone}>
          Annuleren
        </button>
      </div>
    </form>
  );
}

/** MJOP-voorstellen na afronden (§8): pas na overnemen verandert het MJOP. */
function Proposals({ id }: { id: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: me } = useMe();
  const key = ["condition-proposals", id];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<ConditionProposalDto[]>(`/condition/assessments/${id}/proposals`),
  });
  const canAccept = hasModule(me, "mjop", "edit");
  const canReject = hasModule(me, "condition", "edit");
  async function act(path: string, ok: string) {
    try {
      const r = await api<ConditionProposalDto[]>(path, { method: "POST" });
      if (r.length) qc.setQueryData(key, r);
      void qc.invalidateQueries({ queryKey: ["mjop"] });
      toast(ok);
    } catch (e) {
      toast(errMsg(e));
    }
  }
  const list = q.data ?? [];
  const open = list.filter((p) => p.status === "open").length;
  return (
    <section className="panel stack" aria-label="MJOP-voorstellen">
      <p className="hint">
        Voorstellen voor een ander vervangingsjaar op basis van de conditie. Het MJOP verandert pas als je een voorstel
        overneemt; dan komt er een verschuiving met deze reden in de post.
      </p>
      {q.isPending ? (
        <Loading />
      ) : !list.length ? (
        <p className="muted">Geen voorstellen: de geplande vervangingen passen bij de conditie.</p>
      ) : (
        <>
          <div className="tscroll">
            <table className="it">
              <thead>
                <tr>
                  <th>Asset en post</th>
                  <th className="num">Gepland</th>
                  <th className="num">Voorstel</th>
                  <th className="num">Score</th>
                  <th>Status</th>
                  <th>
                    <span className="sr-only">Acties</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((p) => (
                  <tr key={p.id}>
                    <th scope="row">
                      {p.asset.code} {p.asset.name}
                      <span className="muted small block">{p.description}</span>
                      <span className="muted small block">{p.reason}</span>
                    </th>
                    <td className="num">{p.currentYear}</td>
                    <td className="num">
                      <strong>{p.proposedYear}</strong>
                    </td>
                    <td className="num">{p.score}</td>
                    <td>{PROPOSAL_STATUS_LABELS[p.status]}</td>
                    <td>
                      {p.status === "open" && (
                        <span className="btnrow">
                          {canAccept && (
                            <button
                              type="button"
                              className="btn small"
                              aria-label={`Voorstel ${p.asset.code} overnemen`}
                              onClick={() => void act(`/condition/proposals/${p.id}/accept`, "Overgenomen in het MJOP")}
                            >
                              <IconCheck size={16} aria-hidden="true" />
                              Overnemen
                            </button>
                          )}
                          {canReject && (
                            <button
                              type="button"
                              className="btn small ghost"
                              aria-label={`Voorstel ${p.asset.code} afwijzen`}
                              onClick={() => void act(`/condition/proposals/${p.id}/reject`, "Voorstel afgewezen")}
                            >
                              Afwijzen
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {canAccept && open > 0 && (
            <div>
              <button
                type="button"
                className="btn primary"
                onClick={() =>
                  void act(
                    `/condition/assessments/${id}/proposals/accept-all`,
                    `${open} voorstel${open === 1 ? "" : "len"} overgenomen`,
                  )
                }
              >
                Alles overnemen ({open})
              </button>
            </div>
          )}
          {!canAccept && open > 0 && <p className="hint">Overnemen in het MJOP vraagt bewerken op de module MJOP.</p>}
        </>
      )}
    </section>
  );
}
